import { useCallback, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { GeoEventReportsMap } from 'components/GeoEventReportsMap';
import {
  StructuredEvacuationsEditLayer,
  STRUCTURED_EVAC_EDIT_LAYER_DEFAULT_PROPS,
  STRUCTURED_EVAC_PENDING_LAYER_DEFAULT_PROPS,
} from 'features/mapping/components/layers/StructuredEvacuationsLayer';
import { FormEvacZoneStatus } from 'shared/types';
import { useHandleEvacZoneClick } from 'features/evacZones/hooks/useHandleEvacZoneClick';
import { useRegionEvacZones } from 'features/evacZones/hooks/useRegionEvacZones';
import { AlertCamerasLayer } from 'features/mapping/components/layers/AlertCamerasLayer';
import { MapType } from 'features/mapping/mapTypePolicy';
import { isMediaAlertCamera } from 'components/MediaForm';
import { REGION_EVAC_ZONES_REFETCH_INTERVAL } from 'features/evacZones/constants';
import { prepareZonesForMapLayers } from 'features/evacZones/utils';
import {
  isFloodingGeoEvent,
  isWildfireGeoEvent,
} from 'features/geoEvents/geoEvent.utils';
import type { Wildfire } from 'features/wildfires/wildfire.types';
import {
  ReportFormValues,
  ReportableGeoEvent,
  GeoEventFormStatus,
} from './types';
import { getGeoEventReportFeatures } from './utils';

type ReportMapProps = {
  geoEvent: ReportableGeoEvent;
  disableStructuredEvacZoneLayerClick?: boolean;
  zoom?: number;
};

export const ReportMap = (props: ReportMapProps): JSX.Element | null => {
  const { geoEvent, disableStructuredEvacZoneLayerClick, zoom } = props;
  const { setValue, watch, control } = useFormContext<ReportFormValues>();

  const status: GeoEventFormStatus = useWatch({
    control,
    name: 'status',
    defaultValue: geoEvent.isActive ? 'active' : 'inactive',
  });

  const wildfireFormData = useWatch({
    control,
    name: ['acreage', 'containment', 'isFps', 'manualDeactivationStarted'],
    disabled: !isWildfireGeoEvent(geoEvent),
    defaultValue: {
      acreage: null,
      containment: null,
      isFps: false,
      manualDeactivationStarted: null,
    },
  });

  const mapGeoEvent = useMemo(() => {
    if (isWildfireGeoEvent(geoEvent)) {
      const [acreage, containment, isFps, manualDeactivationStarted] =
        wildfireFormData;

      const wildfire: Wildfire = {
        ...geoEvent,
        isActive: status === 'active',
        data: {
          ...geoEvent.data,
          acreage,
          containment,
          isFps,
          manualDeactivationStarted,
        },
      };

      return wildfire;
    }

    return {
      ...geoEvent,
      isActive: status === 'active',
    };
  }, [geoEvent, status, wildfireFormData]);

  const { supportsEvacZones } = getGeoEventReportFeatures(mapGeoEvent);

  const media = watch('media');

  const [
    activeEvacuations,
    evacZoneStatuses,
    releasePendingZoneUids,
    temporarilyDisplayEvacZones,
  ] = useWatch({
    control,
    name: [
      'activeEvacuations',
      'evacZoneStatuses',
      'releasePendingZoneUids',
      'temporarilyDisplayEvacZones',
    ],
    disabled: !supportsEvacZones,
  });

  const { regionEvacZones } = useRegionEvacZones({
    regions: geoEvent.regions,
    evacZoneStatuses: geoEvent.evacZoneStatuses,
    refetchInterval: REGION_EVAC_ZONES_REFETCH_INTERVAL,
    enabled: supportsEvacZones,
  });

  const setFormValue = useCallback(
    (fieldName: string, value: FormEvacZoneStatus[]) => {
      // We need to cast here since setValue is strongly typed
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      setValue(fieldName as any, value);
    },
    [setValue],
  );

  const handleEvacZoneClick = useHandleEvacZoneClick({
    regionEvacZones,
    evacZoneStatuses,
    geoEvent:
      isWildfireGeoEvent(geoEvent) || isFloodingGeoEvent(geoEvent)
        ? geoEvent
        : undefined,
    setValue: setFormValue,
  });

  const { evacZones, pendingEvacZones } = useMemo(() => {
    return prepareZonesForMapLayers(
      regionEvacZones,
      evacZoneStatuses,
      releasePendingZoneUids,
    );
  }, [regionEvacZones, evacZoneStatuses, releasePendingZoneUids]);

  const evacZoneClick =
    temporarilyDisplayEvacZones || disableStructuredEvacZoneLayerClick
      ? undefined
      : handleEvacZoneClick;

  const isEvacZonesLayerVisible =
    (evacZones.length > 0 || pendingEvacZones.length > 0) && activeEvacuations;

  return (
    <GeoEventReportsMap
      geoEvent={mapGeoEvent}
      hiddenMapPixels={null}
      mapType={
        isFloodingGeoEvent(geoEvent)
          ? MapType.FloodingReportForm
          : MapType.ReportForm
      }
      showSelectedGeoEventLabel
      zoom={zoom}
    >
      {supportsEvacZones && (
        <>
          <StructuredEvacuationsEditLayer
            {...STRUCTURED_EVAC_EDIT_LAYER_DEFAULT_PROPS}
            visible={isEvacZonesLayerVisible}
            evacZones={evacZones}
            onClick={evacZoneClick}
          />
          <StructuredEvacuationsEditLayer
            {...STRUCTURED_EVAC_PENDING_LAYER_DEFAULT_PROPS}
            visible={isEvacZonesLayerVisible}
            evacZones={pendingEvacZones}
            onClick={evacZoneClick}
          />
        </>
      )}
      <AlertCamerasLayer
        visible
        interactive={false}
        withLabels
        selectedCameraId={isMediaAlertCamera(media) ? media.id : undefined}
      />
    </GeoEventReportsMap>
  );
};
